// Handle currency/country fetching

import { getFlagUrl } from "../helpers/currency";

const fallbackCountryMap = {
  USD: "US",
  XAF: "CM",
  EUR: "FR",
  GBP: "GB",
};

const LOCAL_STORAGE_KEY = "currencyCountryCache_v2";
const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30mins

const currencyCache = {};
const iso2Cache = {};

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;
const COUNTRY_FETCH_URL = `${BACKEND_BASE_URL}/api/v1/user/country/iso2`;
const COUNTRY_FETCH_URL_USING_CURRENCY = `${BACKEND_BASE_URL}/api/v1/user/country/currency`;

// Load local cache if fresh
const getCacheData = async (type) => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (raw) {
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp < CACHE_EXPIRY_MS) {
        type == 'currencyCache' ? Object.assign(currencyCache, data) : Object.assign(iso2Cache, data);
        return true;
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        return false;
      }
    }
    return false;
  } catch (err) {
    // console.warn("Failed to load or parse currency cache:", err);
    return false;
  }
}

const saveCache = (cacheValue) => {
  try {
    const payload = {data: cacheValue, timestamp: Date.now()};
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // console.warn("Failed to save currency cache:", err);
  }
};

const fetchWithRetry = async (url, retries = 3, delay = 500) => {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return await res.json();
  } catch (err) {
    if (retries <= 0) throw err;
    await new Promise((res) => setTimeout(res, delay));
    return fetchWithRetry(url, retries - 1, delay * 2);
  }
};

export const getCountryByCurrency = async (currencyCode) => {
  const cache = await getCacheData('currencyCache');
  if (cache && currencyCache[currencyCode]) return currencyCache[currencyCode];
  try {
    const response = await fetchWithRetry(`${COUNTRY_FETCH_URL_USING_CURRENCY}/${currencyCode}`);
    const country = await response.data;
    if(!country) return { 
      country_code: null, currency_name: null, 
      currency_symbol: null, currency_code: null 
    };
    const country_code = country?.iso2_code || null;
    const currency_info = country?.currency;
    const result = { 
      country_code: country_code, 
      currency_name: currency_info?.name,
      currency_symbol: currency_info?.symbol,
      currency_code: currency_info?.code,
    };
    currencyCache[currencyCode] = result;
    saveCache(currencyCache);
    return result;
  } catch (err) {
    // console.error(`Error fetching info for currency ${currencyCode}:`, err);
    return { 
      country_code: null, currency_name: null, 
      currency_symbol: null, currency_code: null 
    };
  }
};

export const getCountryByISO2 = async (iso2) => {
  const cache = await getCacheData('iso2Cache');
  if (cache && iso2Cache[iso2]) return iso2Cache[iso2];
  try {
    const response = await fetchWithRetry(`${COUNTRY_FETCH_URL}/${iso2}`);
    const country = await response.data;
    if(!country) return { 
      currencyName: null, symbol: null, flagUrl: null, country: null,
    };
    const country_code = country?.iso2_code || null;
    const currency_info = country?.currency;
    const result = { 
      country_code: country_code, 
      currency_name: currency_info?.name,
      currency_symbol: currency_info?.symbol,
      currency_code: currency_info?.code,
      currencyKey: currency_info,
      currencyName: currency_info?.name,
      symbol: currency_info?.symbol,
      flagUrl: getFlagUrl(country_code),
      country: country,
    };
    iso2Cache[iso2] = result;
    saveCache(iso2Cache);
    return result;
  } catch (err) {
    // console.error(`Failed to fetch country for ISO2 ${iso2}`, err);
    return null;
  }
};